import {
  h,
  spec,
  list,
  remap,
  rec,
  variant,
  text,
  DOMProperty,
  AttributeStore,
  route
} from 'forest'
import {
  createEvent,
  Store,
  Node,
  is,
  combine,
  createStore,
  sample,
  createApi
} from 'effector'
import {
  RuntimeOn,
  RuntimeEvent,
  RuntimeEffect,
  RuntimeStore,
  RuntimePrimeUnit,
  RuntimeSample,
  RuntimeForward,
  RuntimeGuard
} from './index.h'
import {SampleSnap, ID} from './snap.h'

import {updateGroup, createGroup, GroupRecord} from './group'

import {Board} from './board'

// try {
//   let nests = [] as any[]
//   const events = new EventSource('https://localhost:8080/events')
//   events.onmessage = event => {
//     const parsedData = JSON.parse(event.data)
//     console.log('message', event, parsedData)

//     nests = nests.concat(parsedData)
//   }
// } catch (err) {
//   console.error(err)
// }

declare const css: (
  words: TemplateStringsArray,
  ...values: Array<DOMProperty | AttributeStore>
) => void
declare const createGlobalStyle: (
  words: TemplateStringsArray,
  ...values: Array<DOMProperty | AttributeStore>
) => void

type TextProperty =
  | DOMProperty
  | AttributeStore
  | Array<DOMProperty | AttributeStore>

type GridColumn = {
  text?: string
  title?: string
  width: string
}

type GridColumnMap = Record<string, GridColumn>

const shadowCardStyle = () => {
  css`
    box-shadow: 0 5px 15px 0 rgba(0, 0, 0, 0.2);
    background: rgba(250, 250, 250, 0.9);
    border-radius: 0.5rem;
    @media (max-width: 800px) {
      & {
        background: transparent;
        box-shadow: none;
      }
    }
  `
}

const gridRowStyle = () => {
  spec({data: {gridTable: 'row'}})
  css`
    @keyframes fadeIn {
      0% {
        opacity: 0;
      }
      100% {
        opacity: 1;
      }
    }

    display: grid;
    grid-template-columns: var(--columns);
    animation: fadeIn ease-out 100ms;
    animation-fill-mode: forwards;

    @media (max-width: 800px) {
      & {
        grid-template-columns: 3fr 1fr 3fr;
        background: rgba(250, 250, 250, 0.9);
        box-shadow: 0 5px 15px 0 rgba(0, 0, 0, 0.2);
        border-radius: 0.5rem;
      }
    }
    @media (max-width: 400px) {
      & {
        grid-template-columns: 1fr 1fr;
      }
    }
    &:not(:last-child) {
      border-bottom: 1px solid #ddd;
    }
    &:first-child {
      border-radius: 0.5rem 0.5rem 0 0;
      background: rgba(204, 204, 204, 0.5);
      font-size: 0.8em;
      font-weight: bold;
      text-shadow: 0 2px 0 #fff;
    }
    &:not(:first-child) {
      font-size: 0.6em;
    }
    @media (max-width: 800px) {
      &:first-child {
        display: none;
      }
    }
  `
}

const gridCellStyle = () => {
  spec({data: {gridTable: 'cell'}})
  css`
    @media (min-width: 801px) {
      &:first-child {
        margin-left: var(--gridRowNesting, 0);
      }
    }
    padding: 0.3rem 0.5rem;
    white-space: nowrap;
    overflow: auto;
    @media (max-width: 800px) {
      & {
        padding: 0 0 1rem 0;
        text-align: center;
      }
    }
    &:not(:last-child) {
      border-right: 1px solid #ddd;
    }
    @media (max-width: 800px) {
      &:before {
        content: attr(data-title);
        font-weight: bold;
        display: block;
        background: rgba(221, 221, 221, 0.8);
        padding: 0.75rem;
        margin-bottom: 0.75rem;
        font-size: 1rem;
      }
      &:first-child {
        grid-column-start: 1;
        grid-column-end: -1;
        border-bottom: 1px solid #ddd;
      }
      &:first-child:before {
        border-radius: 0.5rem 0.5rem 0 0;
      }
    }
    @media (max-width: 400px) {
      &:last-child {
        grid-column-start: 1;
        grid-column-end: -1;
      }
    }
  `
}

function GridTitle(shape: GridColumnMap) {
  h('div', {
    fn() {
      gridRowStyle()
      for (const key in shape) {
        h('div', {
          text: shape[key].text || key,
          fn() {
            gridCellStyle()
          }
        })
      }
    }
  })
}

function GridRowCells<T extends Record<string, GridColumn>>(
  shape: T,
  values: {[K in keyof T]: TextProperty}
) {
  for (const key in values) {
    const column = shape[key]
    h('div', {
      data: {
        title: column.title || column.text || key
      },
      text: values[key],
      fn() {
        gridCellStyle()
      }
    })
  }
}

function columnsVar(shape: GridColumnMap) {
  const results = [] as string[]
  for (const key in shape) {
    results.push(shape[key].width)
  }
  return results.join(' ')
}

const filesTableDef = {
  file: {width: '2fr'},
  eventsAmount: {
    text: 'events amount',
    title: 'events',
    width: '1fr'
  },
  storesAmount: {
    text: 'stores amount',
    title: 'stores',
    width: '1fr'
  },
  effectsAmount: {
    text: 'effects amount',
    title: 'effects',
    width: '1fr'
  },
  forwardsAmount: {
    text: 'forwards amount',
    title: 'forwards',
    width: '1fr'
  }
}

const unitsTableDef = {
  name: {width: '2fr'},
  type: {width: '4rem'},
  sid: {width: '4rem'},
  id: {width: '3rem'},
  file: {width: '2fr'},
  inline: {width: '4rem'},
  createdBy: {width: '8rem'}
}

const samplesTableDef = {
  type: {width: '3rem'},
  clock: {width: '2fr'},
  target: {width: '2fr'},
  file: {width: '2fr'},
  sourceFile: {
    text: 'source file',
    width: '2fr'
  },
  clockFile: {
    text: 'clock file',
    width: '2fr'
  },
  targetFile: {
    text: 'target file',
    width: '2fr'
  },
  targeting: {width: '6rem'}
}

const forwardsTableDef = {
  source: {width: '1fr'},
  target: {width: '1fr'},
  file: {width: '1fr'},
  fromFile: {
    text: 'from file',
    width: '1fr'
  },
  toFile: {
    text: 'to file',
    width: '1fr'
  }
}

const onsTableDef = {
  source: {width: '1fr'},
  clock: {width: '1fr'},
  isReset: {width: '5rem'}
}

type AnyRuntimeUnit =
  | RuntimePrimeUnit
  | RuntimeSample
  | RuntimeForward
  | RuntimeGuard
  | RuntimeOn

type TopLevelRoute =
  | 'units'
  | 'forwards'
  | 'files'
  | 'samples'
  | 'ons'
  | 'fileStruct'
  | 'board'

function initURLRouter() {
  const basepath = '/diagrams/'
  const defaultInitialRoute = 'board' as TopLevelRoute
  const routeNames = [
    'units',
    'files',
    'forwards',
    'samples',
    'ons',
    'fileStruct',
    'board'
  ] as TopLevelRoute[]
  let initialRoute = location.pathname.replace(basepath, '') as TopLevelRoute
  if (!routeNames.includes(initialRoute)) {
    initialRoute = defaultInitialRoute
    history.replaceState({}, '', `${basepath}${defaultInitialRoute}`)
  }
  const currentRoute = createStore<TopLevelRoute>(initialRoute)
  currentRoute.updates.watch(value => {
    requestAnimationFrame(() => {
      history.replaceState({}, '', `${basepath}${value}`)
    })
  })
  const go = createApi(currentRoute, {
    files: () => 'files',
    units: () => 'units',
    forwards: () => 'forwards',
    samples: () => 'samples',
    ons: () => 'ons',
    fileStruct: () => 'fileStruct',
    board: () => 'board'
  })
  return {
    currentRoute,
    go
  }
}

const router = initURLRouter()

const buttonStyle = () => {
  css`
    @media (any-pointer: coarse) {
      & {
        min-width: 30vmin;
        min-height: 5vmax;
        -webkit-appearance: unset;
      }
    }
  `
}

export function UnitReport({
  ons,
  samples,
  forwards,
  primeUnits,
  guards
}: {
  ons: Store<RuntimeOn[]>
  samples: Store<RuntimeSample[]>
  forwards: Store<RuntimeForward[]>
  primeUnits: Store<RuntimePrimeUnit[]>
  guards: Store<RuntimeGuard[]>
}) {
  function findUnitInList(list: RuntimePrimeUnit[], id: ID) {
    return list.find(({node}) => node.meta.unitId === id)
  }
  function findPrimeUnit(unitId: Store<ID>) {
    return combine(primeUnits, unitId, findUnitInList)
  }
  const tableStyle = (tableDef: GridColumnMap) => {
    spec({
      data: {gridTable: 'table'},
      styleVar: {columns: columnsVar(tableDef)}
    })
    shadowCardStyle()
    css`
      & {
        display: grid;
        margin: 0 auto;
      }
      @media (max-width: 800px) {
        & {
          grid-row-gap: 2rem;
        }
      }
    `
  }
  h('section', {
    data: {section: 'UnitReport'},
    fn() {
      css`
        padding: 5vmin;
        font-family: sans-serif;
        font-size: 20px;
        @media screen and (max-width: 1000px) {
          & {
            padding: 2vh;
          }
        }
        @supports (padding-top: env(safe-area-inset-top, 0)) and
          (padding-top: clamp(0px, 0px, 0px)) {
          @media screen and (max-width: 1000px) {
            & {
              padding-top: clamp(env(safe-area-inset-top, 0), 2vmin, 2vh);
              padding-bottom: clamp(env(safe-area-inset-bottom, 0), 2vmin, 2vh);
              padding-left: clamp(env(safe-area-inset-left, 0), 2vmin, 2vw);
              padding-right: clamp(env(safe-area-inset-right, 0), 2vmin, 2vw);
            }
          }
        }

        & > h1 {
          text-shadow: 0 2px 1px rgba(255, 255, 255, 0.3);
          text-align: center;
          letter-spacing: -0.05rem;
          color: #222;
          margin: 0;
        }

        & > h2 {
          text-align: start;
          margin: 2rem 0 1rem 0;
          color: #555;
        }
      `
      let onId = 0
      const unitMap = combine(
        primeUnits,
        forwards,
        guards,
        ons,
        samples,
        (
          primeUnits,
          forwards,
          guards,
          ons,
          samples
        ): Record<string, AnyRuntimeUnit> => {
          const result = {} as Record<string, AnyRuntimeUnit>
          for (const e of primeUnits) {
            result[e.node.meta.unitId] = e
          }
          for (const e of forwards) {
            result[e.node.meta.unitId] = e
          }
          for (const e of guards) {
            result[e.node.meta.unitId] = e
          }
          for (const e of ons) {
            //@ts-ignore
            e.node = {
              meta: {unitId: `on_${(++onId).toString(36)}`, createdBy: 'on'}
            }
            result[e.node.meta.unitId] = e
          }
          for (const e of samples) {
            e.node.meta.unitId = `sample_${(++onId).toString(36)}`
            result[e.node.meta.unitId] = e
          }
          return result
        }
      )
      const allUnits = combine(
        primeUnits,
        samples,
        forwards,
        ons,
        (primeUnits, samples, forwards, ons): AnyRuntimeUnit[] => [
          ...primeUnits.filter(({node}) => !node.meta.named),
          // ...samples,
          ...forwards
          // ...ons
        ]
      )
      const sortedByFile = allUnits.map(list =>
        list
          .filter(({node}) => !node.meta.named)
          .sort((a, b) =>
            (a.node.meta.file || '').localeCompare(b.node.meta.file || '')
          )
      )
      const unitsByFile = sortedByFile.map(update => {
        const {groups} = updateGroup({
          update,
          state: createGroup({
            getGroupKey: ({node}: RuntimePrimeUnit) => node.meta.file || ''
          })
        })
        return [...groups].sort((a, b) => a.groupKey.localeCompare(b.groupKey))
      })
      const moreUnitsByFile = combine(
        primeUnits,
        samples,
        forwards,
        ons,
        (primeUnits, samples, forwards, ons) => {
          const anyUnit = [
            ...primeUnits.filter(({node}) => !node.meta.named),
            ...samples,
            ...forwards
            // ...ons
          ]
          const sorted = anyUnit
            .filter(({node}) => !node.meta.named)
            .sort((a, b) =>
              (a.node.meta.file || '').localeCompare(b.node.meta.file || '')
            )
          const {groups} = updateGroup({
            update: sorted,
            state: createGroup({
              getGroupKey: ({node}: RuntimePrimeUnit) => node.meta.file || ''
            })
          })
          return [...groups].sort((a, b) =>
            a.groupKey.localeCompare(b.groupKey)
          )
        }
      )
      const filesByDir = unitsByFile.map(update => {
        const {groups} = updateGroup({
          update,
          state: createGroup({
            getGroupKey({groupKey}: GroupRecord<AnyRuntimeUnit>) {
              const filePath = groupKey.split(/\//g)
              return filePath.slice(0, -1).join('/')
            }
          })
        })
        return [...groups].sort((a, b) => a.groupKey.localeCompare(b.groupKey))
      })
      for (const field in router.go) {
        h('button', {
          text: ['/', field],
          handler: {click: router.go[field]},
          fn: buttonStyle
        })
      }
      const hideUnsupportedCases = createStore(true)
      variant({
        source: router.currentRoute.map(name => ({name})),
        key: 'name',
        cases: {
          ons() {
            h('h1', {text: 'on'})
            h('div', {
              fn() {
                tableStyle(onsTableDef)
                GridTitle(onsTableDef)
                list(ons, ({store}) => {
                  h('div', {
                    fn() {
                      gridRowStyle()
                      GridRowCells(onsTableDef, {
                        source: remap(store, 'source'),
                        clock: remap(store, 'clock'),
                        isReset: store.map(
                          ({isReset}) => (isReset ? 'true' : '') as string
                        )
                      })
                    }
                  })
                })
              }
            })
          },
          samples() {
            h('h1', {text: 'sample'})
            h('div', {
              fn() {
                tableStyle(samplesTableDef)
                GridTitle(samplesTableDef)
                const sampleSnaps = samples.map(list =>
                  list.map(({node}) => node.meta.snap as SampleSnap)
                )
                list(sampleSnaps, ({store}) => {
                  const targetUnit = findPrimeUnit(remap(store, 'targetId'))
                  const targetFile = targetUnit.map(
                    ({node}) => node.meta.file || ''
                  )
                  const sourceFile = combine(
                    primeUnits,
                    store,
                    (list, {sourceType}) => {
                      if (sourceType.type !== 'single') return ''
                      const sourceNode = findUnitInList(list, sourceType.id)
                      if (!sourceNode) return ''
                      return sourceNode.node.meta.file || ''
                    }
                  )
                  const clockNullable = combine(
                    primeUnits,
                    store,
                    (list, {clockType}) => {
                      if (clockType.type === 'implicit') return null
                      return findUnitInList(list, clockType.id)
                    }
                  )
                  const clockName = clockNullable.map(clock =>
                    clock ? clock.name : '~/ - /~'
                  )
                  const clockFile = clockNullable.map(clock =>
                    clock ? clock.node.meta.file || '' : ''
                  )
                  const isSupported = store.map(
                    ({sourceType, clockType, targeting}) => {
                      if (sourceType.type !== 'single') return false
                      if (clockType.type === 'implicit') return false
                      if (targeting === 'new unit') return false
                      return true
                    }
                  )
                  const isVisible = combine(
                    hideUnsupportedCases,
                    isSupported,
                    (hide, isSupported) => {
                      if (!hide) return true
                      return isSupported
                    }
                  )
                  h('div', {
                    visible: isVisible,
                    fn() {
                      gridRowStyle()
                      GridRowCells(samplesTableDef, {
                        type: store.map(
                          value => value.sourceType.type as string
                        ),
                        clock: clockName,
                        target: remap(targetUnit, 'name'),
                        file: remap(store, 'file'),
                        sourceFile,
                        clockFile,
                        targetFile,
                        targeting: remap(store, 'targeting')
                      })
                    }
                  })
                })
              }
            })
          },
          board() {
            Board({
              unitMap,
              filesByDir,
              unitsByFile: moreUnitsByFile
            })
          },
          fileStruct() {
            function countColumnSize(
              units: AnyRuntimeUnit[],
              {groupKey, values}: GroupRecord<AnyRuntimeUnit>
            ) {
              const rowSizes = [(groupKey.length * 0.6) | 0] as number[]
              for (const unit of values) {
                switch (unit.unit) {
                  case 'store':
                  case 'event':
                  case 'effect':
                    rowSizes.push(readMeta(unit.node, 'name', '').length)
                    break
                  case 'forward': {
                    const fromId = readMeta(unit.node, 'depsMap').from
                    const from = units.find(
                      ({node}) => readMeta(node, 'unitId') === fromId
                    )
                    if (from) {
                      rowSizes.push(
                        readMeta(from.node, 'name', '').length + 'from: '.length
                      )
                    }
                    const toId = readMeta(unit.node, 'depsMap').to
                    const to = units.find(
                      ({node}) => readMeta(node, 'unitId') === toId
                    )
                    if (to) {
                      rowSizes.push(
                        readMeta(to.node, 'name', '').length + 'to: '.length
                      )
                    }
                    break
                  }
                }
              }
              return Math.max(...rowSizes.map(n => (((n + 4) / 16) | 0) + 1))
            }
            const pageStyle = {
              app() {
                css`
                  margin-top: 0.25rem;
                  display: grid;
                  /* grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); */
                  grid-gap: 1rem;
                  grid-auto-flow: dense;
                  grid-auto-columns: max-content;
                  --max-columns: 10;
                  @media (max-width: 1200px) {
                    & {
                      --max-columns: 7;
                    }
                  }
                  @media (max-width: 800px) {
                    & {
                      --max-columns: 5;
                    }
                  }
                  @media (max-width: 400px) {
                    & {
                      --max-columns: 3;
                    }
                  }
                `
              },
              smallShadow() {
                css`
                  box-shadow: 0 1px 0 rgba(9, 45, 66, 0.25);
                `
              },
              column(columnWidth: Store<number>) {
                css`
                  grid-column: span ${columnWidth};

                  @supports (grid-column: span min(1)) {
                    grid-column: span min(${columnWidth}, var(--max-columns));
                  }
                `
              },
              dir: {
                box(cardWidth: Store<number>) {
                  pageStyle.column(cardWidth)
                  pageStyle.smallShadow()
                  css`
                    /* display: contents; */
                    display: none;
                    &[data-dir-show-borders] {
                      --min-column-size: calc(140px - 0.5rem * 2);
                      grid-template-columns: repeat(
                        auto-fit,
                        minmax(var(--min-column-size), 1fr)
                      );
                      display: grid;
                      grid-template-rows: 1.5rem 1fr;
                      grid-template-columns: repeat(
                        auto-fit,
                        minmax(140px, 1fr)
                      );
                      /* width: max-content; */
                      padding: 0.5rem;
                      border-radius: 0.2rem;
                      background-color: #f0f3f4;
                    }
                  `
                  spec({data: {dir: 'box'}})
                },
                head() {
                  pageStyle.columnTitle()
                  css`
                    display: contents;
                    &[data-dir-show-borders] {
                      display: grid;
                    }
                  `
                  spec({data: {dir: 'head'}})
                },
                body(cardWidth: Store<number>) {
                  pageStyle.column(cardWidth)
                  css`
                    --min-column-size: calc(140px - 0.5rem * 2);
                    display: contents;
                    /* grid-template-columns: repeat(
                      auto-fit,
                      minmax(var(--min-column-size), 1fr)
                    ); */
                    grid-row: 2;
                    grid-gap: 1rem;
                    grid-auto-flow: column;
                    &[data-dir-show-borders] {
                      display: grid;
                    }
                    @media (max-width: 400px) {
                      & {
                        grid-auto-flow: dense;
                      }
                    }
                  `
                  spec({data: {dir: 'body'}})
                }
              },
              file: {
                box(cardWidth: Store<number>) {
                  pageStyle.column(cardWidth)
                  pageStyle.smallShadow()
                  css`
                    border-radius: 0.2rem;
                    padding: 0.5rem;
                    background-color: #dfe3e6;
                    display: grid;
                    grid-template-rows: 1.5rem;
                    grid-gap: 0.5rem;
                    grid-auto-rows: max-content;
                  `
                  spec({data: {file: 'box'}})
                },
                bodyItem() {
                  pageStyle.smallShadow()
                  css`
                    background-color: white;
                    border-radius: 0.25rem;
                    padding: 0.5rem;
                    overflow: hidden;
                    display: grid;
                    justify-content: start;
                    & > div {
                      color: #17394d;
                      font-weight: 400;
                      font-size: 16px;
                      overflow: hidden;
                      text-overflow: ellipsis;
                      white-space: nowrap;
                    }
                  `
                  spec({data: {file: 'bodyItem'}})
                },
                itemTag(color) {
                  pageStyle.smallShadow()
                  css`
                    font-size: 0.75rem;
                    padding: 0.1rem 0.5rem;
                    border-radius: 0.25rem;
                    font-weight: 700;
                    color: white;
                    background-color: ${color};
                    grid-row: 1;
                    width: max-content;
                  `
                },
                title() {
                  pageStyle.columnTitle()
                  css`
                    align-self: center;
                  `
                }
              },
              columnTitle() {
                css`
                  color: #17394d;
                  font-weight: 700;
                  font-size: 0.9rem;
                  padding: 0 0.25rem;
                `
              }
            }
            h('h1', {text: 'App structure'})
            h('section', {
              fn() {
                pageStyle.app()
                list({
                  source: filesByDir,
                  key: 'groupKey',
                  fn({store: dirGroup, key: dirName}) {
                    const dirShowBorders = dirGroup.map(
                      ({groupKey: dirName, values}) =>
                        dirName.length > 0 && values.length > 1
                    )
                    const dirWidth = combine(
                      allUnits,
                      dirGroup,
                      (units, {values}) => {
                        let width = 0
                        for (const fileGroup of values) {
                          width += countColumnSize(units, fileGroup)
                        }
                        return width
                      }
                    )
                    const unitsByFile = remap(dirGroup, 'values')

                    h('div', {
                      data: {dirShowBorders},
                      fn() {
                        pageStyle.dir.box(dirWidth)
                        h('div', {
                          text: dirName,
                          data: {dirShowBorders},
                          fn: pageStyle.dir.head
                        })
                        h('div', {
                          data: {dirShowBorders},
                          fn() {
                            pageStyle.dir.body(dirWidth)
                            list({
                              source: unitsByFile,
                              key: 'groupKey',
                              fn({store: fileGroup, key: fileName}) {
                                const visible = fileName.map(key => key !== '')
                                const fileUnits = remap(fileGroup, 'values')
                                const cardWidth = combine(
                                  allUnits,
                                  fileGroup,
                                  (units, fileGroup) =>
                                    countColumnSize(units, fileGroup)
                                )
                                const style = {
                                  tagColors: [
                                    '#61bd4f',
                                    '#c377e0',
                                    '#f1c150',
                                    '#eb5a46',
                                    '#00c2e0'
                                  ],
                                  themeColors: {
                                    event: '#0D66FF'
                                  }
                                }

                                h('article', {
                                  visible,
                                  fn() {
                                    pageStyle.file.box(cardWidth)
                                    h('header', {
                                      text: fileName,
                                      fn: pageStyle.file.title
                                    })
                                    list(fileUnits, ({store}) => {
                                      h('div', () => {
                                        pageStyle.file.bodyItem()
                                        const name = store.map(({node}) =>
                                          readMeta(node, 'name', '')
                                        )
                                        const createdBy = store.map(({node}) =>
                                          readMeta(node, 'createdBy', '')
                                        )
                                        const ignoredMethods = [
                                          'createStore',
                                          'createEvent',
                                          'createEffect',
                                          'domain.createStore',
                                          'domain.createEvent',
                                          'domain.createEffect'
                                        ]
                                        const showCreatedByTag = createdBy.map(
                                          e =>
                                            !!e &&
                                            e !== '' &&
                                            !ignoredMethods.includes(e)
                                        )
                                        const CreatedBy = () => {
                                          h('span', {
                                            text: createdBy,
                                            visible: showCreatedByTag,
                                            fn() {
                                              pageStyle.file.itemTag(
                                                style.tagColors[2]
                                              )
                                            }
                                          })
                                        }
                                        variant({
                                          source: store,
                                          key: 'unit',
                                          cases: {
                                            store({store}) {
                                              h('span', {
                                                text: 'store',
                                                fn() {
                                                  pageStyle.file.itemTag(
                                                    style.tagColors[0]
                                                  )
                                                }
                                              })
                                              CreatedBy()
                                              h('div', {text: name})
                                            },
                                            event({store}) {
                                              h('span', () => {
                                                pageStyle.file.itemTag(
                                                  style.themeColors.event
                                                )
                                                spec({text: 'event'})
                                              })
                                              CreatedBy()
                                              h('div', {text: name})
                                            },
                                            effect({store}) {
                                              h('span', () => {
                                                pageStyle.file.itemTag(
                                                  style.tagColors[1]
                                                )
                                                spec({text: 'effect'})
                                              })
                                              CreatedBy()
                                              h('div', {text: name})
                                            },
                                            forward({store}) {
                                              const from = combine(
                                                allUnits,
                                                store,
                                                (units, {node}) => {
                                                  const fromId = readMeta(
                                                    node,
                                                    'depsMap'
                                                  ).from
                                                  const from = units.find(
                                                    ({node}) =>
                                                      readMeta(
                                                        node,
                                                        'unitId'
                                                      ) === fromId
                                                  )
                                                  if (!from) return ''
                                                  return readMeta(
                                                    from.node,
                                                    'name',
                                                    ''
                                                  )
                                                }
                                              )
                                              const to = combine(
                                                allUnits,
                                                store,
                                                (units, {node}) => {
                                                  const toId = readMeta(
                                                    node,
                                                    'depsMap'
                                                  ).to
                                                  const to = units.find(
                                                    ({node}) =>
                                                      readMeta(
                                                        node,
                                                        'unitId'
                                                      ) === toId
                                                  )
                                                  if (!to) return ''
                                                  return readMeta(
                                                    to.node,
                                                    'name',
                                                    ''
                                                  )
                                                }
                                              )
                                              h('span', () => {
                                                pageStyle.file.itemTag(
                                                  style.tagColors[3]
                                                )
                                                spec({text: 'forward'})
                                              })
                                              h('div', {
                                                text: ['from: ', from]
                                              })
                                              h('div', {text: ['to: ', to]})
                                            },
                                            __({store}) {
                                              h('div', {text: '??'})
                                            }
                                          }
                                        })
                                      })
                                    })
                                  }
                                })
                              }
                            })
                          }
                        })
                      }
                    })
                  }
                })
              }
            })
          },
          files() {
            h('h1', {text: 'Files report'})
            h('h2', {text: 'files'})
            h('div', {
              fn() {
                tableStyle(filesTableDef)
                GridTitle(filesTableDef)
                list({
                  source: unitsByFile,
                  key: 'groupKey',
                  fn({store, key: groupKey}) {
                    function hideZero(num: number) {
                      if (num === 0) return ''
                      return num.toString()
                    }
                    const isNonEmptyFile = groupKey.map(value => value !== '')
                    const values = remap(store, 'values')
                    const eventsAmount = values.map(list => {
                      const events = list.filter(e => e.unit === 'event')
                      return hideZero(events.length)
                    })
                    const storesAmount = values.map(list =>
                      hideZero(list.filter(e => e.unit === 'store').length)
                    )
                    const effectsAmount = values.map(list =>
                      hideZero(list.filter(e => e.unit === 'effect').length)
                    )
                    const forwardsAmount = values.map(list =>
                      hideZero(list.filter(e => e.unit === 'forward').length)
                    )
                    h('div', {
                      styleVar: {gridRowNesting: '0rem'},
                      visible: isNonEmptyFile,
                      fn() {
                        gridRowStyle()
                        GridRowCells(filesTableDef, {
                          file: groupKey,
                          eventsAmount,
                          storesAmount,
                          effectsAmount,
                          forwardsAmount
                        })
                      }
                    })
                  }
                })
              }
            })
            list({
              source: unitsByFile,
              key: 'groupKey',
              fn({store, key}) {
                // const values = remap(store, 'values')
                // const forwards = values.map(list =>
                //   list.filter(e => e.unit === 'forward')
                // )
                const fileUnits = combine(allUnits, key, (units, file) =>
                  units.filter(({node}) => node.meta.file === file)
                )
                const fileUnitsIds = fileUnits.map(units =>
                  units.map(({node}) => node.meta.unitId as string)
                )
                const unitsDependentOnFile = combine(
                  allUnits,
                  fileUnitsIds,
                  (units, ids) =>
                    units.filter(({node, unit}) => {
                      const {depsMap, createdBy} = node.meta
                      if (unit === 'forward') {
                        return (
                          ids.includes(depsMap.from) || ids.includes(depsMap.to)
                        )
                      }
                      if (createdBy === 'combine') {
                        return Object.values(
                          depsMap.source
                        ).some((id: string) => ids.includes(id))
                      }
                      return false
                      // if (!node.meta.depsMap) return
                    })
                )
                const filesDependentOnFile = unitsDependentOnFile.map(units => {
                  const files = new Set<string>()
                  for (const {node} of units) {
                    const file = node.meta.file
                    if (file) files.add(file)
                  }
                  return [...files]
                })
                h('h2', {text: key})
                list(filesDependentOnFile, ({store}) => {
                  h('p', {text: store})
                })
              }
            })
          },
          forwards() {
            h('h1', {text: 'forwards'})
            h('div', {
              fn() {
                tableStyle(forwardsTableDef)
                GridTitle(forwardsTableDef)
                list(forwards, ({store}) => {
                  const fromFile = combine(allUnits, store, (units, {node}) => {
                    const {from} = node.meta.depsMap
                    const referencedUnit = units.find(
                      ({node}) => node.meta.unitId === from
                    )
                    if (referencedUnit && referencedUnit.node.meta.file)
                      return referencedUnit.node.meta.file
                    return ''
                  })
                  const toFile = combine(allUnits, store, (units, {node}) => {
                    const {to} = node.meta.depsMap
                    const referencedUnit = units.find(
                      ({node}) => node.meta.unitId === to
                    )
                    if (referencedUnit && referencedUnit.node.meta.file)
                      return referencedUnit.node.meta.file
                    return ''
                  })
                  const isSupported = combine(
                    fromFile,
                    toFile,
                    (from, to) => from !== '' && to !== ''
                  )
                  const isVisible = combine(
                    hideUnsupportedCases,
                    isSupported,
                    (hide, isSupported) => {
                      if (!hide) return true
                      return isSupported
                    }
                  )
                  h('div', {
                    visible: isVisible,
                    fn() {
                      gridRowStyle()
                      GridRowCells(forwardsTableDef, {
                        source: remap(store, 'source'),
                        target: remap(store, 'target'),
                        file: remap(store, 'file'),
                        fromFile,
                        toFile
                      })
                    }
                  })
                })
              }
            })
          },
          units() {
            h('h1', {text: 'Unit report'})

            h('h2', {text: 'units'})
            const nestedRowsVisible = createStore(true)

            h('div', {
              fn() {
                tableStyle(unitsTableDef)
                GridTitle(unitsTableDef)

                type UnitRowItem = {unit: AnyRuntimeUnit; nesting: number}
                const UnitRow = rec<UnitRowItem>(({state}) => {
                  const [store, nesting] = remap(state, [
                    'unit',
                    'nesting'
                  ] as const)
                  const namedChilds = combine(
                    primeUnits,
                    store,
                    nesting,
                    (units, owner, nesting): UnitRowItem[] => {
                      const ids = owner.node.meta.namedChilds
                      if (!ids) return []
                      return units
                        .filter(({node}) => ids.includes(node.meta.unitId))
                        .map(unit => ({
                          unit,
                          nesting: nesting + 1
                        }))
                    }
                  )
                  h('div', {
                    styleVar: {
                      gridRowNesting: nesting.map(nesting => `${nesting}rem`)
                    },
                    visible: combine(
                      nesting,
                      nestedRowsVisible,
                      (nesting, visible) => {
                        if (nesting === 0) return true
                        return visible
                      }
                    ),
                    fn() {
                      gridRowStyle()
                      GridRowCells(unitsTableDef, {
                        name: store.map(value => {
                          //prettier-ignore
                          switch (value.unit) {
                                case 'forward': return ''
                                case 'guard': return ''
                                case 'on': return ''
                                case 'sample': return ''
                                default: return value.name
                              }
                        }),
                        type: remap(store, 'unit') as Store<string>,
                        sid: store.map(({node}) => node.meta.sid || ''),
                        id: store.map(({node}) => node.meta.unitId || ''),
                        file: store.map(({node}) => node.meta.file || ''),
                        inline: store.map(
                          ({node}) =>
                            (node.meta.inlined ? 'true' : '') as string
                        ),
                        createdBy: store.map(
                          ({node}) => node.meta.createdBy || ''
                        )
                      })
                    }
                  })
                  list(namedChilds, ({store}) => {
                    UnitRow({state: store})
                  })
                })
                list(sortedByFile, ({store}) => {
                  UnitRow({state: store.map(unit => ({unit, nesting: 0}))})
                  // variant({
                  //   source: store,
                  //   key: ({node}) => {
                  //     const childs = node.meta.namedChilds
                  //     if (!childs || childs.length === 0) return 'no'
                  //     return 'yes'
                  //   },
                  //   cases: {
                  //     yes({store}) {
                  //       const childs = combine(primeUnits, store, (list, {node}) =>
                  //         list.filter(item =>
                  //           node.meta.namedChilds.includes(item.node.meta.unitId)
                  //         )
                  //       )
                  //       list(childs, ({store}) => {
                  //         UnitRow({state: store})
                  //       })
                  //     },
                  //     __({store}) {}
                  //   }
                  // })
                })
              }
            })
          }
        }
      })
    }
  })
}

function readMeta(node: Node, field: string): any
function readMeta<T>(node: Node, field: string, defaultValue: T): T
function readMeta(node: Node, field: string, defaultValue?: any) {
  const val = node.meta[field]
  if (val === undefined) return defaultValue
  return val
}
